import {Box} from "@cdx/ds";
import {useEffect, useMemo, useRef} from "react";
import {adaptToGrid, BushRect, getBoundingBox, TwoNums, VisionBoardBag} from "./canvas-utils";
import {getCanvasNodeRef} from "./vision-board-node-selection-store";
import {NodeBushEntry, NodeMoveArg} from "./useVisionBoardData";
import {MultiNodeMenu} from "./CanvasMenu";
import {BaseResizeHandlers} from "./CanvasNodeResizeHandles";
import {VisionBoardNode, VisionBoardNodeId} from "../../cdx-models/utils/extended-fields";
import {useGlobalKeyPress} from "@cdx/common";
import {duplicateBushEntryNode} from "./canvas-node-utils";

const PADDING = 8;

const getFramePos = (bbox: BushRect) => ({
  top: bbox.minY - PADDING,
  left: bbox.minX - PADDING,
  width: bbox.maxX - bbox.minX + PADDING * 2,
  height: bbox.maxY - bbox.minY + PADDING * 2,
});

const getNextPos = (node: NodeBushEntry, arg: NodeMoveArg | undefined): BushRect => {
  if (!arg) return node;
  switch (arg.type) {
    case "move":
      return {
        minX: node.minX + arg.value[0],
        minY: node.minY + arg.value[1],
        maxX: node.maxX + arg.value[0],
        maxY: node.maxY + arg.value[1],
      };
    case "rect":
      return arg.value;
  }
};

const getNextRect = (
  rect: BushRect,
  node: NodeBushEntry,
  orgRect: BushRect,
  bag: VisionBoardBag,
  ratioW: number,
  ratioH: number
) => {
  const orgDMinX = node.minX - orgRect.minX;
  const orgDMaxX = node.maxX - orgRect.maxX;
  const orgDMinY = node.minY - orgRect.minY;
  const orgDMaxY = node.maxY - orgRect.maxY;

  const getTypeAndRatio = (): ["free" | "fixed" | "none", ratio: number, minDims: TwoNums] => {
    switch (node.node.get("type") as VisionBoardNode["type"]) {
      case "image":
        const imgValue = node.node.get("value") as (VisionBoardNode & {type: "image"})["value"];
        return ["fixed", imgValue.width / imgValue.height, [20, 20]];
      case "text":
        return ["free", 1, [160, 40]];
      case "filter": {
        const {mode} = node.node.get("value") as (VisionBoardNode & {type: "filter"})["value"];
        switch (mode) {
          case "pie":
            return ["fixed", 1, [20, 20]];
          case "horBar":
            return ["free", 1, [100, 20]];
          case "verBar":
            return ["free", 1, [20, 100]];
          case "table":
            return ["free", 1, [120, 60]];
          default:
            return ["free", 1, [120, 60]];
        }
      }
      default:
        return ["none", 1, [100, 100]];
    }
  };
  const [type, ratio, minDims] = getTypeAndRatio();

  switch (type) {
    case "free": {
      const [minX, minY] = adaptToGrid(
        [rect.minX + orgDMinX * ratioW, rect.minY + orgDMinY * ratioH],
        bag.store
      );
      const maxDims = adaptToGrid(
        [rect.maxX + orgDMaxX * ratioW, rect.maxY + orgDMaxY * ratioH],
        bag.store
      );
      const maxX = Math.max(minX + minDims[0], maxDims[0]);
      const maxY = Math.max(minY + minDims[1], maxDims[1]);
      return {minX, minY, maxX, maxY};
    }
    case "fixed": {
      const [minX, minY] = adaptToGrid(
        [rect.minX + orgDMinX * ratioW, rect.minY + orgDMinY * ratioH],
        bag.store
      );
      const orgAspect = (orgRect.maxX - orgRect.minX) / (orgRect.maxY - orgRect.minY);
      const currAspect = (rect.maxX - rect.minX) / (rect.maxY - rect.minY);

      const getWH = () => {
        if (orgAspect > currAspect) {
          const w = Math.max(minDims[0], rect.maxX + orgDMaxX * ratioW - minX);
          const h = w / ratio;
          return [w, h];
        } else {
          const h = Math.max(minDims[1], rect.maxY + orgDMaxY * ratioH - minY);
          const w = h * ratio;
          return [w, h];
        }
      };
      const [nextW, nextH] = getWH();
      const [maxX, maxY] = adaptToGrid([minX + nextW, minY + nextH], bag.store);
      return {minX, minY, maxX, maxY};
    }
    case "none": {
      const fullWidth = orgRect.maxX - orgRect.minX;
      const fullHeight = orgRect.maxY - orgRect.minY;
      const w = node.maxX - node.minX;
      const h = node.maxY - node.minY;
      const getMinX = () => {
        const gap = fullWidth - w;
        const xRatio = gap === 0 ? 0 : orgDMinX / gap;
        return rect.minX + (rect.maxX - rect.minX - w) * xRatio;
      };
      const getMinY = () => {
        const gap = fullHeight - h;
        const yRatio = gap === 0 ? 0 : orgDMinY / gap;
        return rect.minY + (rect.maxY - rect.minY - h) * yRatio;
      };
      const [minX, minY] = adaptToGrid([getMinX(), getMinY()], bag.store);
      return {minX, minY, maxX: minX + w, maxY: minY + h};
    }
    default:
      throw new Error(`Don't know how to bulk resize ${type}`);
  }
};

const MultiSelectResizeHandles = ({
  orgRect,
  bag,
  nodes,
}: {
  orgRect: BushRect;
  bag: VisionBoardBag;
  nodes: NodeBushEntry[];
}) => {
  const orgW = orgRect.maxX - orgRect.minX;
  const orgH = orgRect.maxY - orgRect.minY;
  const setPos = (rect: BushRect, mode: "move" | "end" | "cancel") => {
    const nextW = rect.maxX - rect.minX;
    const nextH = rect.maxY - rect.minY;
    const ratioH = nextH / orgH;
    const ratioW = nextW / orgW;
    for (const node of nodes) {
      const nodeRef = getCanvasNodeRef(node.id);
      const el = nodeRef?.current;
      if (!el) continue;
      const next = getNextRect(rect, node, orgRect, bag, ratioW, ratioH);

      el.style.left = `${next.minX}px`;
      el.style.top = `${next.minY}px`;
      el.style.width = `${next.maxX - next.minX}px`;
      el.style.height = `${next.maxY - next.minY}px`;
      bag.store.emitNodeMoveListener(node.id, {
        type: "rect",
        value: next,
      });
    }
    if (mode === "end")
      bag.store.doc.transact(() => {
        for (const node of nodes) {
          const next = getNextRect(rect, node, orgRect, bag, ratioW, ratioH);
          node.node.set("pos", {
            ...node.node.get("pos"),
            x: next.minX,
            y: next.minY,
            w: next.maxX - next.minX,
            h: next.maxY - next.minY,
          });
        }
      });
  };
  return <BaseResizeHandlers setPos={setPos} orgPos={orgRect} bag={bag} minDims={[20, 20]} />;
};

const ActiveMultiSelectBox = ({bag, nodes}: {bag: VisionBoardBag; nodes: NodeBushEntry[]}) => {
  const bbox = useMemo(() => getBoundingBox(nodes), [nodes]);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return bag.store.registerNodeMoveListener(
      nodes.map((n) => n.id),
      (changedMap) => {
        const nextRects = nodes.map((n) => getNextPos(n, changedMap.get(n.id)));
        const nextFrame = getFramePos(getBoundingBox(nextRects));
        const nodeStyle = nodeRef.current!.style;
        nodeStyle.top = nextFrame.top + "px";
        nodeStyle.left = nextFrame.left + "px";
        nodeStyle.width = nextFrame.width + "px";
        nodeStyle.height = nextFrame.height + "px";
      }
    );
  }, [nodes, bag.store]);

  const handleDuplicate = () => {
    const idSet = new Set(nodes.map((n) => n.id));
    const allEdges = bag.store.getEdgesSnaphot(bbox);
    const edges = allEdges.filter(
      (e) => idSet.has(e.source.node.id) && idSet.has(e.target.node.id)
    );
    const oldToNewIds = new Map<VisionBoardNodeId, VisionBoardNodeId>();
    const nextIds = bag.store.doc.transact(() => {
      const newNodeIds = nodes.map((node) => {
        const newId = duplicateBushEntryNode(bag, node).id;
        oldToNewIds.set(node.id, newId);
        return newId;
      });
      edges.forEach((edge) => {
        const newSourceId = oldToNewIds.get(edge.source.node.id)!;
        const newTargetId = oldToNewIds.get(edge.target.node.id)!;
        if (newSourceId && newTargetId) {
          bag.store.addEdge({
            source: {nodeId: newSourceId, side: edge.source.side},
            target: {nodeId: newTargetId, side: edge.target.side},
          });
        }
      });
      return newNodeIds;
    });
    bag.selectionStore.setNodeIds(nextIds);
  };
  useGlobalKeyPress({fn: handleDuplicate, code: "KeyD", withMod: true});

  return (
    <Box
      ref={nodeRef}
      absolute
      pointerEvents="none"
      rounded={4}
      border="1px"
      style={{
        background: "rgba(30, 30, 30, 0.1)",
        ...getFramePos(bbox),
      }}
    >
      <MultiNodeMenu nodes={nodes} bag={bag} rect={bbox} />
      <MultiSelectResizeHandles orgRect={bbox} bag={bag} nodes={nodes} />
    </Box>
  );
};

type SelectionBoxProps = {
  bag: VisionBoardBag;
};
const MultiSelectBox = ({bag}: SelectionBoxProps) => {
  const nodeIds = bag.selectionStore.useSelectedNodes((idSet) =>
    idSet.size > 1 ? [...idSet] : null
  );
  if (!nodeIds) return null;
  const nodes = nodeIds.map((id) => bag.store.getNode(id)!).filter(Boolean);
  if (nodes.length < 2) return null;
  return <ActiveMultiSelectBox nodes={nodes} bag={bag} />;
};

export default MultiSelectBox;
